import React from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import styles from "../../../walkTestSecondStep.module.css";
import DateTimeField from "../../../DateTimeField";
import {
    sleepSectionLabelStyle,
    sleepSubLabelStyle,
    sleepPrimaryButtonStyle,
} from "../../sleepReportTheme";
import {
    // Dialog,
    // DialogContent,
    Typography,
    Button,
    // IconButton,
} from "@mui/material";
import axios from "../../../../../utils/lib/axios";

const validationMessages = {
    device: "Please select a device",
    startDate: "Please enter a valid date",
    startTime: "Please enter a valid time",
    endDate: "Please enter a valid date",
    endTime: "Please enter a valid time",
};

const todayDateString = new Date().toISOString().split("T")[0];

const validationSchema = Yup.object().shape({
    device: Yup.string().required(validationMessages.device),
    startDate: Yup.string()
        .required(validationMessages.startDate)
        .test(
            "start-date-not-in-future",
            "Start date cannot be in the future",
            (value) => !value || value <= todayDateString,
        ),
    startTime: Yup.string().required(validationMessages.startTime),
    endDate: Yup.string()
        .required(validationMessages.endDate)
        .test(
            "end-date-not-in-future",
            "End date cannot be in the future",
            (value) => !value || value <= todayDateString,
        )
        .test(
            "end-date-not-before-start-date",
            "End date cannot be less than start date",
            function (value) {
                const { startDate } = this.parent;
                if (!value || !startDate) return true;
                // Both are `YYYY-MM-DD`, so lexicographic comparison is safe.
                return value >= startDate;
            },
        ),
    endTime: Yup.string()
        .required(validationMessages.endTime)
        .test(
            "end-time-not-before-start-time",
            "End time cannot be less than start time",
            function (value) {
                const { startDate, endDate, startTime } = this.parent;
                if (!value || !startTime || !startDate || !endDate)
                    return true;
                // Only enforce time ordering when dates are the same.
                if (endDate !== startDate) return true;
                // Both are `HH:MM` (browser time input), lexicographic compare is safe.
                return value >= startTime;
            },
        ),
});

const defaultInitialValues = {
    device: "",
    startDate: new Date().toISOString().split("T")[0],
    startTime: "",
    endDate: new Date().toISOString().split("T")[0],
    endTime: "",
};

const sectionLabelStyle = sleepSectionLabelStyle;

const subLabelStyle = sleepSubLabelStyle;

const deviceLabelStyle = {
    ...sleepSubLabelStyle,
    fontSize: "20px",
    fontWeight: 500,
    width: "80px",
};

/** Out-of-flow so showing validation text does not grow the form / modal. */
const fieldErrorOverlayBase = {
    position: "absolute",
    left: 0,
    top: "100%",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#d32f2f",
    fontSize: "12px",
    zIndex: 2,
    maxWidth: "min(280px, 85vw)",
};

const errorIconStyle = {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#d32f2f",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
};

const toUtcISOStringFromDateAndTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "";

    // `date` input gives `YYYY-MM-DD` and `time` input gives `HH:MM`.
    // Build a local datetime (no timezone suffix) then convert to UTC ISO.
    const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const localDate = new Date(`${dateStr}T${normalizedTime}`);
    return localDate.toISOString();
};

/** @returns {number|null} */
const localDateTimeToMs = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const t = new Date(`${dateStr}T${normalizedTime}`).getTime();
    return Number.isNaN(t) ? null : t;
};

function FormValidityWatcher({
    submitAttempted,
    setSubmitAttempted,
    clearToast,
}) {
    const { isValid } = useFormikContext();

    React.useEffect(() => {
        if (submitAttempted && isValid) {
            clearToast();
            setSubmitAttempted(false);
        }
    }, [clearToast, isValid, setSubmitAttempted, submitAttempted]);

    return null;
}

export default function VerifySleepDataForm({
    devices,
    setFormData,
    setShowVerifiedSleepData,
    formData,
    setAlertDetails,
}) {
    const formikRef = React.useRef();
    const toastTimeoutRef = React.useRef(null);
    const [noRecordsLock, setNoRecordsLock] = React.useState(false);
    const [lockedQueryKey, setLockedQueryKey] = React.useState("");
    const [submitAttempted, setSubmitAttempted] = React.useState(false);

    const getQueryKey = React.useCallback(
        (values) =>
            [
                values?.device || "",
                values?.startDate || "",
                values?.startTime || "",
                values?.endDate || "",
                values?.endTime || "",
            ].join("|"),
        [],
    );

    React.useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    const showToast = (message, severity, autoClose = true) => {
        setAlertDetails?.({
            message,
            severity,
            showAlert: true,
            autoClose,
        });

        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        if (autoClose) {
            // Auto-close toast after a short delay
            toastTimeoutRef.current = setTimeout(() => {
                setAlertDetails?.({
                    message: "",
                    severity: "error",
                    showAlert: false,
                    autoClose: true,
                });
            }, 7000);
        }
    };

    const clearToast = React.useCallback(() => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
            toastTimeoutRef.current = null;
        }
        setAlertDetails?.({
            message: "",
            severity: "error",
            showAlert: false,
            autoClose: true,
        });
    }, [setAlertDetails]);

    const unlockIfQueryChanged = React.useCallback(
        (nextValues) => {
            if (!noRecordsLock) return;
            if (getQueryKey(nextValues) === lockedQueryKey) return;

            setNoRecordsLock(false);
            setLockedQueryKey("");
            clearToast();
        },
        [clearToast, getQueryKey, lockedQueryKey, noRecordsLock],
    );

    const handleQueryFieldChange = React.useCallback(
        (field, value, values, setFieldValue) => {
            setSubmitAttempted(false);
            setFieldValue(field, value);
            unlockIfQueryChanged({
                ...values,
                [field]: value,
            });
        },
        [unlockIfQueryChanged],
    );

    const handleFormSubmit = (values, { setSubmitting }) => {
        setNoRecordsLock(false);
        setLockedQueryKey("");

        const failRange = (message) => {
            showToast(message, "error", false);
            setSubmitting(false);
        };

        const startMs = localDateTimeToMs(values.startDate, values.startTime);
        const endMs = localDateTimeToMs(values.endDate, values.endTime);

        if (startMs == null || endMs == null) {
            failRange(
                "Please enter valid start and end date and time.",
            );
            return;
        }

        if (endMs <= startMs) {
            failRange(
                "End date and time must be after start date and time.",
            );
            return;
        }

        const maxEndMs = startMs + 24 * 60 * 60 * 1000;
        if (endMs > maxEndMs) {
            failRange(
                "Sleep end must be within 24 hours of sleep start.",
            );
            return;
        }

        return handleVerify(values);
    };

    const handleVerify = async (values) => {
        // Preserve device_id in `device`, but also pass the selected device label
        // so the next step can display a human readable name.
        const selectedDevice = devices?.find((d) => d.value === values?.device);
        const deviceLabel =
            selectedDevice?.label ??
            values?.deviceLabel ??
            values?.device ??
            "";

        setFormData?.({
            ...values,
            deviceLabel,
        });

        try {
            const response = await axios.post(
                "/sleep/check-records",

                {
                    device_id: values.device,
                    sleep_start: toUtcISOStringFromDateAndTime(
                        values.startDate,
                        values.startTime,
                    ),
                    sleep_end: toUtcISOStringFromDateAndTime(
                        values.endDate,
                        values.endTime,
                    ),
                },
                //TODO: Hardcoded values for now, will be replaced with the actual values later
                // {
                //     device_id: "436504df-6f8b-4aa7-bb75-4364ab854fa2", //"e8e1d21c-b6e9-4c2d-b50f-670813d6976f",
                //     sleep_start: `2026-03-07T16:30:00.000Z`,
                //     sleep_end: `2026-03-08T23:30:00.000Z`,
                // },
            );

            if (response.data?.data?.has_records) {
                showToast("Data verified for the selected range.", "success");
                setNoRecordsLock(false);
                setLockedQueryKey("");

                setShowVerifiedSleepData?.(true);
            } else {
                setNoRecordsLock(true);
                setLockedQueryKey(getQueryKey(values));
                showToast(
                    "No data found for the selected device and time range.",
                    "error",
                    false,
                );
            }
        } catch (error) {
            setNoRecordsLock(false);
            setLockedQueryKey("");
            showToast(
                error?.response?.data?.message ||
                    "Something went wrong while verifying sleep data. Please try again.",
                "error",
            );
        }
    };

    return (
        <Formik
            // key={formData ? "edit" : "new"}
            innerRef={formikRef}
            initialValues={formData || defaultInitialValues}
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}
            enableReinitialize
            validateOnMount
            validateOnChange
        >
            {({
                errors,
                touched,
                isSubmitting,
                values,
                isValid,
                setFieldValue,
                setTouched,
                submitForm,
                validateForm,
            }) => (
                <Form>
                    <FormValidityWatcher
                        submitAttempted={submitAttempted}
                        setSubmitAttempted={setSubmitAttempted}
                        clearToast={clearToast}
                    />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "40px",
                            height: "100%",
                            marginTop: "12px",
                        }}
                    >
                        <div
                            style={{
                                position: "relative",
                                alignSelf: "flex-start",
                            }}
                        >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                    }}
                                >
                                    <Typography
                                        style={deviceLabelStyle}
                                        className={`${styles.label}`}
                                    >
                                        Device
                                    </Typography>
                                    <Field
                                        name="device"
                                        as="select"
                                        className={styles.sleepDeviceSelect}
                                        onChange={(e) =>
                                            handleQueryFieldChange(
                                                "device",
                                                e.target.value,
                                                values,
                                                setFieldValue,
                                            )
                                        }
                                    >
                                        <option value="">Select Device</option>
                                        {devices?.map((device) => (
                                            <option
                                                key={device?.id}
                                                value={device?.value}
                                            >
                                                {device?.label}
                                            </option>
                                        ))}
                                    </Field>
                                </div>
                                {errors.device && touched.device && (
                                    <div
                                        style={{
                                            ...fieldErrorOverlayBase,
                                            left: "92px",
                                        }}
                                    >
                                        <span style={errorIconStyle}>!</span>
                                        {errors.device}
                                    </div>
                                )}
                            </div>

                            <div>
                                <Typography
                                    style={sectionLabelStyle}
                                    className={`${styles.label}`}
                                >
                                    Start Time
                                </Typography>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "16px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Typography style={subLabelStyle}>
                                                Date
                                            </Typography>
                                            <Field name="startDate">
                                                {({ field }) => (
                                                    <DateTimeField
                                                        type="date"
                                                        className={
                                                            styles.sleepFormInputWide
                                                        }
                                                        max={todayDateString}
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            handleQueryFieldChange(
                                                                "startDate",
                                                                e.target.value,
                                                                values,
                                                                setFieldValue,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        {errors.startDate &&
                                            touched.startDate && (
                                                <div
                                                    style={{
                                                        ...fieldErrorOverlayBase,
                                                        left: "56px",
                                                    }}
                                                >
                                                    <span
                                                        style={errorIconStyle}
                                                    >
                                                        !
                                                    </span>
                                                    {errors.startDate}
                                                </div>
                                            )}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Typography style={subLabelStyle}>
                                                Time
                                            </Typography>
                                            <Field name="startTime">
                                                {({ field }) => (
                                                    <DateTimeField
                                                        type="time"
                                                        step="1"
                                                        className={
                                                            styles.sleepFormInputWide
                                                        }
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            handleQueryFieldChange(
                                                                "startTime",
                                                                e.target.value,
                                                                values,
                                                                setFieldValue,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        {errors.startTime &&
                                            touched.startTime && (
                                                <div
                                                    style={{
                                                        ...fieldErrorOverlayBase,
                                                        left: "56px",
                                                    }}
                                                >
                                                    <span
                                                        style={errorIconStyle}
                                                    >
                                                        !
                                                    </span>
                                                    {errors.startTime}
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Typography
                                    style={sectionLabelStyle}
                                    className={`${styles.label}`}
                                >
                                    End Time
                                </Typography>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "16px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Typography style={subLabelStyle}>
                                                Date
                                            </Typography>
                                            <Field name="endDate">
                                                {({ field }) => (
                                                    <DateTimeField
                                                        type="date"
                                                        className={
                                                            styles.sleepFormInputWide
                                                        }
                                                        min={
                                                            values?.startDate ||
                                                            ""
                                                        }
                                                        max={todayDateString}
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            handleQueryFieldChange(
                                                                "endDate",
                                                                e.target.value,
                                                                values,
                                                                setFieldValue,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        {errors.endDate && touched.endDate && (
                                            <div
                                                style={{
                                                    ...fieldErrorOverlayBase,
                                                    left: "56px",
                                                }}
                                            >
                                                <span style={errorIconStyle}>
                                                    !
                                                </span>
                                                {errors.endDate}
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Typography style={subLabelStyle}>
                                                Time
                                            </Typography>
                                            <Field name="endTime">
                                                {({ field }) => (
                                                    <DateTimeField
                                                        type="time"
                                                        step="1"
                                                        className={
                                                            styles.sleepFormInputWide
                                                        }
                                                        min={
                                                            values?.endDate ===
                                                            values?.startDate
                                                                ? values?.startTime ||
                                                                  ""
                                                                : ""
                                                        }
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            handleQueryFieldChange(
                                                                "endTime",
                                                                e.target.value,
                                                                values,
                                                                setFieldValue,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        {errors.endTime && touched.endTime && (
                                            <div
                                                style={{
                                                    ...fieldErrorOverlayBase,
                                                    left: "56px",
                                                }}
                                            >
                                                <span style={errorIconStyle}>
                                                    !
                                                </span>
                                                {errors.endTime}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        <div style={{ marginTop: "auto" }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    alignItems: "flex-end",
                                }}
                            >
                                <Button
                                    type="button"
                                    onClick={async () => {
                                        const validationErrors =
                                            await validateForm();
                                        const formIsInvalid =
                                            Object.keys(validationErrors)
                                                .length > 0;

                                        if (formIsInvalid) {
                                            setSubmitAttempted(true);
                                            setTouched(
                                                {
                                                    device: true,
                                                    startDate: true,
                                                    startTime: true,
                                                    endDate: true,
                                                    endTime: true,
                                                },
                                                true,
                                            );
                                            showToast(
                                                "Please complete all fields.",
                                                "error",
                                                false,
                                            );
                                            return;
                                        }

                                        setSubmitAttempted(false);
                                        submitForm();
                                    }}
                                    disabled={
                                        isSubmitting ||
                                        noRecordsLock ||
                                        (submitAttempted && !isValid)
                                    }
                                    style={{
                                        ...sleepPrimaryButtonStyle,
                                        width: "196px",
                                        height: "48px",
                                        opacity:
                                            isSubmitting ||
                                            noRecordsLock ||
                                            (submitAttempted && !isValid)
                                                ? "0.7"
                                                : "1",
                                    }}
                                >
                                    Verify
                                </Button>
                                <div
                                    style={{
                                        marginTop: "0px",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        width: "100%",
                                    }}
                                >
                                    <p style={{ margin: "8px 0 0" }}>
                                        Check if data is available in the
                                        selected time frame
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}
